import { THUMB_DOMAIN } from '@constants';
import useEditingStore from '@hooks/useEditingStore';
import useToggle from '@hooks/useToggle';
import {
  $categoryList,
  $categoryMap,
  $curSceneId,
  $sceneMap,
  $templateId,
  $tourData,
} from '@stores/dataStores';
import {
  $editingCategoryConfig,
  $editingCustomButton,
  $editingFlag,
  $editingHiddenIdSet,
  $editingUIColor,
} from '@stores/editingStores';
import { $isCn } from '@stores/langStores';
import { $GateCategoryHeight, $uiCategoryShowApi } from '@stores/uiStores';
import {
  type CategoryData,
  EditConfigLanguageType,
  type GeneralID,
  type SceneData,
  TemplateId,
} from '@types';
import { modifyUiValue } from '@utils/modifyUiValue';
import classNames from 'classnames';
import { useGate, useStore } from 'effector-react';
// @ts-ignore
import Menu from 'pano-menu';
import { toFlag } from 'pano-utils';
/* eslint-disable no-plusplus */
import { useEffect, useMemo } from 'react';
import { useResizeDetector } from 'react-resize-detector';

type Scene = Omit<SceneData, 'id'> & {
  id?: GeneralID;
};

function CategoryWidget() {
  const categoryConfig = useStore($editingCategoryConfig.store);
  const flag = useStore($editingFlag.store);
  const ui = useStore($editingUIColor.store);
  const templateId = useStore($templateId);
  if (categoryConfig) {
    return <CategoryMenu isOpen={!!categoryConfig.enabledColumn} />;
  }
  if (ui) {
    return <CategoryMenu isOpen />;
  }
  if (templateId === TemplateId.metaverseA && flag) {
    return <CategoryMenu isOpen />;
  }
  if (templateId === TemplateId.project) {
    return <CategoryMenu isOpen />;
  }
  return null;
}

function CategoryMenu({ isOpen }: { isOpen: boolean }) {
  const hiddenIdSet = useStore($editingHiddenIdSet.store);
  const templateId = useStore($templateId);
  const ui = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.config?.ui ?? {},
    $editing: $editingUIColor.store,
  });
  const customButton = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.customButton,
    $editing: $editingCustomButton.store,
  });
  const categoryConfig = useEditingStore({
    $data: $tourData,
    dataFn: (state) => state.categoryConfig,
    $editing: $editingCategoryConfig.store,
  });
  const cList = useStore($categoryList);
  const cMap = useStore($categoryMap);
  const sMap = useStore($sceneMap);
  const curId = useStore($curSceneId);
  const menuToggle = useToggle(true);
  const isMenuOpen = isOpen && menuToggle.isOn;
  const isCn = useStore($isCn);
  const { height, ref } = useResizeDetector();

  useEffect(() => {
    $uiCategoryShowApi.set(isOpen);
    return () => {
      $uiCategoryShowApi.set(false);
    };
  }, [isOpen]);

  useGate($GateCategoryHeight, height);

  const { originalCategory, originalScenes } = useMemo(() => {
    const category: CategoryData[] = [];
    const scenes: Scene[] = [];
    let newId = 1;
    let sort = 0;
    for (let i = 0; i < cList.length; i++) {
      const cItem = cMap.get(cList[i])!;
      const newCId = newId++;
      if (cItem.categoryIds.length) {
        const children: CategoryData[] = [];
        for (let j = 0; j < cItem.categoryIds.length; j++) {
          const subItem = cMap.get(cItem.categoryIds[j])!;
          const newSubId = newId++;
          for (let k = 0; k < subItem.sceneIds.length; k++) {
            const sceneItem = sMap.get(subItem.sceneIds[k]);
            if (sceneItem) {
              sort++;
              scenes.push({
                ...sceneItem,
                categoryId: newSubId,
                sort,
                showThumb: toFlag(!hiddenIdSet.has(sceneItem.id)),
              });
            }
          }
          children.push({ id: newSubId, name: subItem.name });
        }
        category.push({ id: newCId, name: cItem.name, children });
      } else {
        for (let m = 0; m < cItem.sceneIds.length; m++) {
          const sceneItem = sMap.get(cItem.sceneIds[m]);
          if (sceneItem) {
            sort++;
            scenes.push({
              ...sceneItem,
              categoryId: newCId,
              sort,
              showThumb: toFlag(!hiddenIdSet.has(sceneItem.id)),
            });
          }
        }
        category.push({ id: newCId, name: cItem.name });
      }
    }
    return { originalCategory: category, originalScenes: scenes };
  }, [cMap, sMap, cList, hiddenIdSet]);

  const renderContent = () => {
    return (
      <Menu
        onOpen={() => menuToggle.on()}
        onClose={
          templateId === TemplateId.realEstateA
            ? () => {}
            : () => menuToggle.off()
        }
        buttonTitle={
          categoryConfig?.buttonTitle || (isCn ? '场景选择' : 'List')
        }
        originalCategory={originalCategory}
        scenes={originalScenes}
        scenesId={curId}
        isShow={isMenuOpen}
        onSceneChange={() => {}}
        payScenes={[]}
        THUMB_DOMAIN={THUMB_DOMAIN}
        disableTip
        imgSuffix={(size: number) =>
          `?x-oss-process=image/auto-orient,1/resize,s_${size},m_lfit`
        }
        templateId={templateId}
        ui={modifyUiValue(ui)}
        language={isCn ? EditConfigLanguageType.cn : EditConfigLanguageType.en} // 1中文, 2英文
        // 简洁模版下没有自定义按钮时的场景开关按钮
        extData={{ existCustomButton: !!customButton?.length }}
      />
    );
  };

  if (templateId === TemplateId.project) {
    return renderContent();
  }

  return (
    <div
      className={classNames('absolute', {
        'right-0 left-0 bottom-100': [
          TemplateId.free,
          TemplateId.vip,
          TemplateId.legacy,
        ].includes(templateId),
        'right-0 left-0 bottom-0': [TemplateId.metaverseA].includes(templateId),
        'bottom-65 w-full':
          templateId === TemplateId.simple && customButton?.length,
        'bottom-0 w-full':
          templateId === TemplateId.simple && !customButton?.length,
        'bottom-75 w-[900px]':
          templateId === TemplateId.realEstateA && customButton?.length,
        'bottom-24 w-[900px]':
          templateId === TemplateId.realEstateA && !customButton?.length,
        'bottom-70 w-[500px]':
          templateId === TemplateId.realEstateB && customButton?.length,
        'bottom-10 w-[500px]':
          templateId === TemplateId.realEstateB && !customButton?.length,
      })}
      ref={ref}
    >
      {renderContent()}
    </div>
  );
}

export default CategoryWidget;
